// src/components/EngineerJobDetail.js
import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { 
  doc, 
  onSnapshot, 
  updateDoc, 
  Timestamp, 
  getDoc 
} from "firebase/firestore";
import DynamicJobForm from "./DynamicJobForm";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Stack,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";
import { formatDateTime } from "../utils/formatHelpers"; // Assuming you'll create this

function EngineerJobDetail({ jobId, onBack }) {
  const [job, setJob] = useState(null);
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showJobSheet, setShowJobSheet] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!jobId) return;
    const jobRef = doc(db, "jobs", jobId);
    const unsub = onSnapshot(jobRef, async (snap) => {
      if (snap.exists()) {
        const jobData = { id: snap.id, ...snap.data() };
        setJob(jobData);
        if (jobData.jobTemplateId) {
          try {
            const tRef = doc(db, "jobSheetTemplates", jobData.jobTemplateId);
            const tSnap = await getDoc(tRef);
            if (tSnap.exists()) setTemplate(tSnap.data());
          } catch (e) { console.error(e); }
        }
        if (jobData.completionData) setFormData(jobData.completionData);
        setLoading(false);
      } else {
        setSnackbar({ open: true, message: "Job not found", severity: "error" });
        setLoading(false);
      }
    });
    return () => unsub();
  }, [jobId]);

  const handleStatusUpdate = async (newStatus, timestampField) => {
    try {
      const updateData = { status: newStatus };
      if (timestampField) updateData[timestampField] = Timestamp.now();
      await updateDoc(doc(db, "jobs", jobId), updateData);
      setSnackbar({ open: true, message: `Status updated to ${newStatus}`, severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to update status", severity: "error" });
    }
  };

  const handleCompleteJob = async (completedFormData) => {
    setIsCompleting(true);
    try {
      await updateDoc(doc(db, "jobs", jobId), {
        status: "Completed",
        completedTime: Timestamp.now(),
        completionData: completedFormData || formData,
      });
      setSnackbar({ open: true, message: "Job completed successfully!", severity: "success" });
      setTimeout(onBack, 2000);
    } catch (e) {
      setIsCompleting(false);
      setSnackbar({ open: true, message: "Failed to complete job", severity: "error" });
    }
  };

  const handleFormDataChange = useCallback(async (data) => {
    setFormData(data);
    try {
      await updateDoc(doc(db, "jobs", jobId), { completionData: data });
    } catch (e) { console.error(e); }
  }, [jobId]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!job) return <Box sx={{ p: 4 }}><Alert severity="error">Job not found</Alert></Box>;

  if (showJobSheet && template?.fields) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#0f172a" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => setShowJobSheet(false)} sx={{ color: "white" }}><ArrowBackIcon /></IconButton>
          <Typography variant="h6" color="white">Job Sheet</Typography>
        </Box>
        <Box sx={{ p: 2, maxWidth: 800, mx: "auto" }}>
          <DynamicJobForm
            fields={template.fields}
            onFormDataChange={handleFormDataChange}
            onComplete={handleCompleteJob}
            jobId={jobId}
            initialData={job.completionData || {}}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0f172a", pb: 4 }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, bgcolor: "rgba(30, 41, 59, 0.7)", position: "sticky", top: 0, zIndex: 10 }}>
        <IconButton onClick={onBack} sx={{ color: "white" }}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} color="white">{job.endCustomerName || job.customerName}</Typography>
          <Typography variant="caption" color="text.secondary">#{job.jobReferenceNumber}</Typography>
        </Box>
        <Chip label={job.status} color={getStatusColor(job.status)} size="small" />
      </Box>

      <Box sx={{ p: 2, maxWidth: 800, mx: "auto" }}>
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">📍 SITE ADDRESS</Typography>
              <Typography variant="body1">{job.endCustomerAddress || job.siteAddress || "N/A"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">👤 SITE CONTACT</Typography>
              <Typography variant="body1">{job.endCustomerContact || job.siteContact || "N/A"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">🚗 VEHICLE</Typography>
              <Typography variant="body1">{job.vehicleReg} ({job.vehicleMake || "N/A"})</Typography>
            </Box>
          </Stack>
        </Paper>

        {(job.jobNotes || job.adminNotes) && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.3)" }}>
            <Typography variant="subtitle2" color="warning.main" gutterBottom>JOB NOTES</Typography>
            <Typography variant="body2">{job.jobNotes || job.adminNotes}</Typography>
          </Paper>
        )}

        {job.status === "Pending" && (
          <Button 
            fullWidth variant="contained" color="success" size="large" sx={{ py: 2 }}
            onClick={() => handleStatusUpdate("Travelling", "acceptedTime")}
          >
            Accept & Start Travel
          </Button>
        )}

        {job.status === "Travelling" && (
          <Button 
            fullWidth variant="contained" color="info" size="large" sx={{ py: 2 }}
            onClick={() => handleStatusUpdate("On Site", "onSiteTime")}
          >
            I have Arrived on Site
          </Button>
        )}

        {job.status === "On Site" && (
          <Button 
            fullWidth variant="contained" color="secondary" size="large" sx={{ py: 2 }}
            onClick={() => handleStatusUpdate("In Progress", "startTime")}
          >
            Start Work
          </Button>
        )}

        {job.status === "In Progress" && !isCompleting && (
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>Work in progress...</Typography>
            <Button 
              fullWidth variant="contained" color="warning" size="large" sx={{ py: 2 }}
              onClick={() => setShowJobSheet(true)}
              startIcon={<AssignmentIcon />}
            >
              Open Job Sheet
            </Button>
          </Paper>
        )}

        {job.status === "Completed" && (
          <Alert severity="success">This job has been completed.</Alert>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default EngineerJobDetail;